import React, { useState } from "react";
import { TextField, Typography } from "@mui/material";
import LegalbirdIoModal from "../Modal/LegalbirdIoModal";
import { MediaObject } from "../../types/MediaObject";
import { apiPost } from "../../services/Api/apiCall";
import { useSnackbar } from "notistack";

import ButtonLoading from "../Button/ButtonLoading";
import { useCurrentUser } from "../../provider/CurrentUserProvider";
import { FILE_SIZE_LIMIT_EXCEEDED } from "../../services/mediaObjectService";

type CopyMediaObjectModalProps = {
  mediaObject: MediaObject;
  updateMediaObjects: Function;
  copyMediaObjectOpen: boolean;
  setCopyMediaObjectOpen: Function;
};

export default function CopyMediaObjectModal({
  mediaObject,
  updateMediaObjects,
  copyMediaObjectOpen,
  setCopyMediaObjectOpen,
}: CopyMediaObjectModalProps) {
  const [attachmentNumber, setAttachmentNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useCurrentUser();

  const createMediaObjectCopy = async () => {
    setIsLoading(true);
    let snackbarMessage = "Dokument wurde erfolgreich als Anlage dupliziert";
    let snackbarVariant: "custom" | "error" = "custom";
    try {
      await apiPost("/lb/services/copy_media_object_as_attachment", {
        mediaObjectId: mediaObject.id,
        attachmentNumber: attachmentNumber,
        createdBy: currentUser["@id"],
      });
    } catch (e) {
      snackbarMessage = "Dokument konnte nicht als Anlage dupliziert werden";
      snackbarVariant = "error";
    }

    enqueueSnackbar(snackbarMessage, {
      variant: snackbarVariant,
      isNonInteractive: true,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
      persist: true,
    });

    updateMediaObjects();
    setIsLoading(false);
    setAttachmentNumber("");
    setCopyMediaObjectOpen(false);
  };

  if (mediaObject.size >= FILE_SIZE_LIMIT_EXCEEDED) {
    return (
      <LegalbirdIoModal
        handleClose={() => setCopyMediaObjectOpen(false)}
        open={copyMediaObjectOpen}
        title={"Als Anlage duplizieren"}
      >
        <Typography>
          Diese Funktion ist nur für Dokumente bis zu einer Dateigröße von 20 MB verfügbar. Die ausgewählte Datei
          überschreitet die maximale Dateigröße. Bitte zunächst die Datei komprimieren.
        </Typography>
      </LegalbirdIoModal>
    );
  }

  return (
    <LegalbirdIoModal
      handleClose={() => {
        setCopyMediaObjectOpen(false);
      }}
      open={copyMediaObjectOpen}
      title={"Als Anlage duplizieren"}
      submitButton={
        <ButtonLoading
          isLoading={isLoading}
          disabled={!attachmentNumber}
          variant={"contained"}
          onClick={createMediaObjectCopy}
        >
          Dokument als Anlage duplizieren
        </ButtonLoading>
      }
    >
      <Typography sx={{
        marginBottom: "16px"
      }}>
        Mit dieser Funktion kann das ausgewählte Dokument als Anlage (beispielsweise für Anlagen von Klageschriften)
        erstellt werden. Hierzu wird das Dokument dupliziert und die Kopie je nach gewählter Nummer im untenstehenden
        Dropdown benannt.
      </Typography>
      <Typography>Bitte auswählen, welche Nummer die Anlage erhalten soll.</Typography>
      <TextField
        type={"number"}
        value={attachmentNumber}
        onChange={({ target }) => setAttachmentNumber(target.value)}
        label={"Nummer der Anlage"}
      />
    </LegalbirdIoModal>
  );
}
